// Every string SaleDeskStrip renders of its own, and the shape a caller may
// replace them with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// The strip's words of its own are the eyebrow over the latest notice and the
// phrase a screen reader hears for the unread badge. The notice's text and its
// lane arrive from the page already in the reader's language, and the count is
// a figure.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{name}` placeholders are filled by the strip, with BidCard's `interpolate`.
 * Upper case is the eyebrow's CSS, not the string's.
 */
export const SALE_DESK_STRIP_TRANSLATIONS = Object.freeze({
  // The eyebrow over the latest notice: who posted it, and the lane it is
  // about, which is the notice's own label ("Lane 1", "All lanes"). The
  // separator is part of the string, as BidCard's `proxyWide` has it, because
  // where it goes is the translation's decision.
  eyebrow: 'Sale desk · {lane}',

  // Heard beside the unread badge, which shows the bare count: a figure alone
  // says nothing to a reader who cannot see what it sits on.
  //
  // TWO COUNT KEYS, and no zero. The badge is not drawn at zero, so nothing is
  // heard then either, and what is left, one against several, is the same
  // category in both target locales — the reasoning is FilterScreen's Apply
  // keys, in filterScreenTranslations.js. Both read identically in English,
  // which does not inflect here; French does.
  unreadOne: '{count} unread',
  unreadSeveral: '{count} unread',
});

/**
 * The keys in `translations` that SaleDeskStrip would not read, as dotted
 * paths.
 *
 * The walk itself is BidCard's, which takes the defaults object as an argument,
 * so this surface passes its own rather than owning a second copy of a
 * comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, SALE_DESK_STRIP_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) {
    return SALE_DESK_STRIP_TRANSLATIONS;
  }

  return { ...SALE_DESK_STRIP_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidSaleDeskStripI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') {
    return false;
  }
  if (Object.keys(i18n).some((key) => key !== 'translations')) {
    return false;
  }
  if (i18n.translations === undefined) {
    return true;
  }
  return unreadTranslationKeys(i18n.translations).length === 0;
}
